import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Save, Ban, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { demoBookingApi } from "../../lib/api";
import { formatApiErrorDetail } from "../../contexts/AuthContext";
import { DInput, DTextarea } from "./ui";

const DAYS = [
  ["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"],
  ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"],
];
const DURATIONS = [15, 30, 45, 60];
const BUFFERS = [0, 15, 30];
const inputCls = "h-9 px-2.5 rounded-lg bg-[#0F1623] border border-white/10 text-[13px] text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/30";

export default function PlatformDemoBooking() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notifText, setNotifText] = useState("");
  const [newOverride, setNewOverride] = useState({ date: "", blocked: true, start: "09:00", end: "17:00" });

  const load = async () => {
    setLoading(true);
    try {
      const data = await demoBookingApi.getSettings();
      setS(data);
      setNotifText((data.notificationEmails || []).join(", "));
    } catch (e) { toast.error("Could not load Demo Booking settings."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setDay = (day, windows) => setS((cur) => ({ ...cur, weeklySchedule: { ...cur.weeklySchedule, [day]: windows } }));
  const addWindow = (day) => setDay(day, [...(s.weeklySchedule[day] || []), { start: "09:00", end: "17:00" }]);
  const updateWindow = (day, i, key, val) => {
    const wins = [...s.weeklySchedule[day]];
    wins[i] = { ...wins[i], [key]: val };
    setDay(day, wins);
  };
  const removeWindow = (day, i) => setDay(day, s.weeklySchedule[day].filter((_, idx) => idx !== i));

  const addOverride = () => {
    if (!newOverride.date) { toast.error("Pick a date for the override."); return; }
    const ov = newOverride.blocked
      ? { date: newOverride.date, blocked: true, windows: [] }
      : { date: newOverride.date, blocked: false, windows: [{ start: newOverride.start, end: newOverride.end }] };
    setS((cur) => ({ ...cur, dateOverrides: [...(cur.dateOverrides || []), ov].sort((a, b) => a.date.localeCompare(b.date)) }));
    setNewOverride({ date: "", blocked: true, start: "09:00", end: "17:00" });
  };
  const removeOverride = (date) => setS((cur) => ({ ...cur, dateOverrides: cur.dateOverrides.filter((o) => o.date !== date) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        timezone: s.timezone,
        weeklySchedule: s.weeklySchedule,
        dateOverrides: s.dateOverrides,
        meetingDurationMin: s.meetingDurationMin,
        bufferMinutes: s.bufferMinutes,
        notificationEmails: notifText.split(",").map((e) => e.trim()).filter(Boolean),
        popupTitle: s.popupTitle,
        popupBody: s.popupBody,
      };
      const updated = await demoBookingApi.updateSettings(payload);
      setS(updated);
      setNotifText((updated.notificationEmails || []).join(", "));
      toast.success("Demo Booking settings saved.");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save settings."); }
    finally { setSaving(false); }
  };

  if (loading || !s) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="h-7 w-7 text-cyan-400 animate-spin" /></div>;
  }

  const titleLen = (s.popupTitle || "").length;

  return (
    <div className="space-y-5 max-w-4xl" data-testid="platform-demo-booking">
      {/* Weekly availability */}
      <section className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
        <div className="text-[13px] font-bold text-white uppercase tracking-wide mb-1">Weekly availability</div>
        <p className="text-[12px] text-slate-500 mb-4">Set the days and time windows the demo calendar can offer. Leave a day empty to make it unavailable.</p>
        <div className="space-y-2.5">
          {DAYS.map(([key, label]) => {
            const windows = s.weeklySchedule[key] || [];
            return (
              <div key={key} className="rounded-lg border border-white/10 p-3" data-testid={`weekly-day-${key}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[13px] font-semibold text-slate-200 w-28">{label}</span>
                  <button onClick={() => addWindow(key)} data-testid={`weekly-day-${key}-add-window`} className="inline-flex items-center gap-1 text-[11.5px] text-cyan-300 hover:text-cyan-200 font-medium">
                    <Plus className="h-3 w-3" /> Add window
                  </button>
                </div>
                {windows.length === 0 ? (
                  <div className="text-[12px] text-slate-600 italic pl-1">Unavailable</div>
                ) : (
                  <div className="space-y-1.5">
                    {windows.map((w, i) => (
                      <div key={i} className="flex items-center gap-2" data-testid={`weekly-day-${key}-window-${i}`}>
                        <input type="time" value={w.start} onChange={(e) => updateWindow(key, i, "start", e.target.value)} data-testid={`weekly-day-${key}-window-${i}-start`} className={inputCls} />
                        <span className="text-slate-500 text-[12px]">to</span>
                        <input type="time" value={w.end} onChange={(e) => updateWindow(key, i, "end", e.target.value)} data-testid={`weekly-day-${key}-window-${i}-end`} className={inputCls} />
                        <button onClick={() => removeWindow(key, i)} data-testid={`weekly-day-${key}-window-${i}-remove`} className="p-1.5 rounded-md text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Date overrides */}
      <section className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
        <div className="text-[13px] font-bold text-white uppercase tracking-wide mb-1">Specific date overrides</div>
        <p className="text-[12px] text-slate-500 mb-4">Block a normally-available date, give it custom hours, or open up a one-off date outside the weekly schedule.</p>

        {(s.dateOverrides || []).length > 0 && (
          <div className="space-y-1.5 mb-3">
            {s.dateOverrides.map((o) => (
              <div key={o.date} className="flex items-center justify-between rounded-lg border border-white/10 px-3 py-2" data-testid={`date-override-${o.date}`}>
                <div className="flex items-center gap-2 text-[13px] text-slate-200">
                  <span className="font-semibold">{o.date}</span>
                  {o.blocked ? (
                    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30"><Ban className="h-3 w-3" /> Blocked</span>
                  ) : (
                    <span className="text-[12px] text-slate-400">{(o.windows || []).map((w) => `${w.start}–${w.end}`).join(", ")}</span>
                  )}
                </div>
                <button onClick={() => removeOverride(o.date)} data-testid={`date-override-${o.date}-remove`} className="p-1.5 rounded-md text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}

        <div className="rounded-lg border border-dashed border-white/15 p-3 space-y-2.5">
          <div className="flex items-center gap-2 flex-wrap">
            <input type="date" value={newOverride.date} onChange={(e) => setNewOverride((s2) => ({ ...s2, date: e.target.value }))} data-testid="override-date-input" className={inputCls} />
            <div className="inline-flex rounded-lg bg-[#0F1623] border border-white/10 p-0.5">
              <button onClick={() => setNewOverride((s2) => ({ ...s2, blocked: true }))} data-testid="override-mode-blocked" className={`px-2.5 py-1 rounded-md text-[12px] font-medium ${newOverride.blocked ? "bg-rose-500/20 text-rose-300" : "text-slate-500"}`}>Block this date</button>
              <button onClick={() => setNewOverride((s2) => ({ ...s2, blocked: false }))} data-testid="override-mode-custom" className={`px-2.5 py-1 rounded-md text-[12px] font-medium ${!newOverride.blocked ? "bg-cyan-500/20 text-cyan-300" : "text-slate-500"}`}>Custom hours</button>
            </div>
            {!newOverride.blocked && (
              <>
                <input type="time" value={newOverride.start} onChange={(e) => setNewOverride((s2) => ({ ...s2, start: e.target.value }))} data-testid="override-start-input" className={inputCls} />
                <span className="text-slate-500 text-[12px]">to</span>
                <input type="time" value={newOverride.end} onChange={(e) => setNewOverride((s2) => ({ ...s2, end: e.target.value }))} data-testid="override-end-input" className={inputCls} />
              </>
            )}
            <button onClick={addOverride} data-testid="date-override-add-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-[12.5px] font-semibold">
              <CalendarPlus className="h-3.5 w-3.5" /> Add override
            </button>
          </div>
        </div>
      </section>

      {/* Duration / buffer / timezone */}
      <section className="rounded-xl border border-white/10 bg-white/[0.02] p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-[11.5px] font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Meeting duration</label>
          <select value={s.meetingDurationMin} onChange={(e) => setS((c) => ({ ...c, meetingDurationMin: Number(e.target.value) }))} data-testid="meeting-duration-select" className={`w-full ${inputCls}`}>
            {DURATIONS.map((d) => <option key={d} value={d}>{d} minutes</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11.5px] font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Buffer between bookings</label>
          <select value={s.bufferMinutes} onChange={(e) => setS((c) => ({ ...c, bufferMinutes: Number(e.target.value) }))} data-testid="buffer-select" className={`w-full ${inputCls}`}>
            {BUFFERS.map((b) => <option key={b} value={b}>{b === 0 ? "No buffer" : `${b} minutes`}</option>)}
          </select>
        </div>
        <DInput label="Timezone" value={s.timezone} onChange={(e) => setS((c) => ({ ...c, timezone: e.target.value }))} testid="demo-booking-timezone-input" placeholder="America/New_York" />
      </section>

      {/* Notification email */}
      <section className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
        <div className="text-[13px] font-bold text-white uppercase tracking-wide mb-1">Notification email</div>
        <p className="text-[12px] text-slate-500 mb-3">Who should be emailed when a demo is booked? Separate multiple addresses with commas.</p>
        <input value={notifText} onChange={(e) => setNotifText(e.target.value)} data-testid="notification-emails-input" placeholder="sales@servicegurupro.com, ceo@servicegurupro.com" className={`w-full ${inputCls}`} />
      </section>

      {/* Popup content */}
      <section className="rounded-xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
        <div className="text-[13px] font-bold text-white uppercase tracking-wide mb-1">Popup content</div>
        <p className="text-[12px] text-slate-500 mb-1">Shown at the top of the booking popup on the website, above the calendar. Plain text only.</p>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-[11.5px] font-semibold text-slate-400 uppercase tracking-wider">Title</label>
            <span className={`text-[11px] font-medium ${titleLen > 80 ? "text-rose-400" : "text-slate-500"}`} data-testid="popup-title-count">{titleLen}/80</span>
          </div>
          <input
            value={s.popupTitle} maxLength={80}
            onChange={(e) => setS((c) => ({ ...c, popupTitle: e.target.value.slice(0, 80) }))}
            data-testid="popup-title-input"
            placeholder="Book a live demo"
            className={`w-full ${inputCls}`}
          />
        </div>
        <DTextarea label="Body" value={s.popupBody} onChange={(e) => setS((c) => ({ ...c, popupBody: e.target.value }))} rows={3} testid="popup-body-textarea" placeholder="Pick a time that works for you and we'll walk you through the platform." />
      </section>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="demo-booking-save-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-[13px] font-semibold disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Demo Booking settings
        </button>
      </div>
    </div>
  );
}
